import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import InfoIcon from '@mui/icons-material/Info';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface VendorDetails {
  vendor_id: string;
  vendor_name: string;
  location: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

interface ServmanInvoice {
  location: string;
  vendor_id: string;
  vendor_name: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
  trantype: string;
  synced_at: string;
}

interface DocuwareInvoice {
  location: string;
  vendor_id: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: number;
  docid: string;
  dw_created_date: string;
  dw_modified_date: string;
  synced_at: string;
}

interface VendorInvoice {
  id: number;
  resolved_vendor_name: string;
  statement_number: string;
  invoice_date: string;
  notes?: string;
  line_items?: any[];
  raw_extracted_text?: string;
  textract_response?: any;
  pdf_url?: string;
  pdf_filename?: string;
  validation_status?: string;
  servman_matched?: boolean;
  docuware_matched?: boolean;
  validated_at?: string;
  location?: string;
  vendor_id?: string;
  vendor_email?: string;
  extraction_error?: string;
  vendor_details?: VendorDetails;
  source_type?: string;
  source_id?: number;
  received_at?: string;
  created_at: string;
  updated_at: string;
}

const VendorInvoiceDetailPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<boolean>(false);
  const [extractedTextModalOpen, setExtractedTextModalOpen] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'text' | 'json'>('text');
  const [revalidating, setRevalidating] = useState<boolean>(false);
  const [hideMatched, setHideMatched] = useState<boolean>(true);
  const [matchDetailModal, setMatchDetailModal] = useState<{
    open: boolean;
    type: 'servman' | 'docuware' | null;
    data: any;
  }>({
    open: false,
    type: null,
    data: null,
  });
  const [vendors, setVendors] = useState<VendorDetails[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorDetails | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    location: '',
    notes: '',
  });

  const fetchVendors = async (searchTerm = '') => {
    setLoadingVendors(true);
    try {
      const response: any = await axios.get('/vendors/servman_vendors', {
        params: { search: searchTerm },
      });
      setVendors(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${id}`);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      setFormData({
        vendor_id: invoiceData.vendor_id || '',
        location: invoiceData.location || '',
        notes: invoiceData.notes || '',
      });
      if (invoiceData.vendor_details) {
        setSelectedVendor(invoiceData.vendor_details);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch invoice details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = (_event: any, newValue: VendorDetails | null) => {
    setSelectedVendor(newValue);
    if (newValue) {
      setFormData({
        ...formData,
        vendor_id: newValue.vendor_id,
        location: newValue.location,
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/vendors/vendor_invoices/${id}`, {
        vendor_invoice: formData,
      });
      showSuccessSnackBar('Invoice updated successfully');
      setEditing(false);
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`/vendors/vendor_invoices/${id}`);
      showSuccessSnackBar('Invoice deleted successfully');
      history.push('/vendors/invoices');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete invoice';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleMarkAsIgnored = async () => {
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/ignore`);
      showSuccessSnackBar('Invoice marked as ignored');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to mark invoice as ignored';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleRevalidate = async () => {
    setRevalidating(true);
    try {
      await axios.post(`/vendors/vendor_invoices/${id}/revalidate`);
      showSuccessSnackBar('Invoice re-validated successfully');
      fetchInvoice();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to re-validate invoice';
      showErrorSnackBar(errorMessage);
    } finally {
      setRevalidating(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
    fetchVendors();
  }, [id]);

  // Set presigned PDF URL when invoice loads
  useEffect(() => {
    if (invoice?.pdf_url) {
      setPdfUrl(invoice.pdf_url);
    }
  }, [invoice?.pdf_url]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  return (
    <Box style={{ width: '100%' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          Back to List
        </Button>
        {invoice.pdf_url && (
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            href={invoice.pdf_url}
            target="_blank"
            size="small"
          >
            Open PDF
          </Button>
        )}
      </Box>

      {invoice.extraction_error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>PDF extraction failed:</strong> {invoice.extraction_error}
        </Alert>
      )}

      {/* Invoice Summary + PDF Embed */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Left Side: Vendor Info & Invoice Details */}
        <Grid item xs={12} md={invoice.pdf_url ? 5 : 12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h5" component="h1" gutterBottom>
                    {invoice.resolved_vendor_name}
                  </Typography>
                </Box>
                {!editing && (
                  <Button variant="contained" color="primary" onClick={() => setEditing(true)} size="small">
                    Edit
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {editing ? (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={vendors}
                        getOptionLabel={(option) => `${option.vendor_name} (${option.vendor_id})`}
                        value={selectedVendor}
                        onChange={handleVendorChange}
                        loading={loadingVendors}
                        onInputChange={(_, newInputValue) => {
                          if (newInputValue.length > 2) {
                            fetchVendors(newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Vendor Lookup"
                            size="small"
                            helperText="Search for vendor by name"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.vendor_id === value.vendor_id}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" gap={2} mt={2}>
                    <Button variant="contained" color="primary" onClick={handleUpdate} size="small">
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          vendor_id: invoice.vendor_id || '',
                          location: invoice.location || '',
                          notes: invoice.notes || '',
                        });
                        setSelectedVendor(invoice.vendor_details || null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Received At
                      </Typography>
                      <Typography variant="body1">
                        {(invoice.received_at || invoice.created_at)
                          ? new Date(invoice.received_at || invoice.created_at).toLocaleString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {invoice.line_items && invoice.line_items.length > 0
                          ? `$${invoice.line_items
                              .reduce((sum: number, li: any) => sum + (parseFloat(li.amount) || 0), 0)
                              .toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                          : '$0.00'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Line Items
                      </Typography>
                      <Typography variant="body1">
                        {invoice.line_items && invoice.line_items.length > 0 ? (
                          <Chip label={`${invoice.line_items.length} items`} size="small" color="default" />
                        ) : (
                          'None'
                        )}
                      </Typography>
                    </Grid>

                    {invoice.source_type === 'outlook_email' && invoice.source_id && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Source Email
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                          onClick={() => history.push(`/vendors/outlook-emails/${invoice.source_id}`)}
                        >
                          View Outlook Email →
                        </Button>
                      </Grid>
                    )}

                    {invoice.notes && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Notes
                        </Typography>
                        <Typography variant="body2">{invoice.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>

                  {invoice.vendor_details && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Vendor Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Vendor Name
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.vendor_name}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Vendor ID
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.vendor_id}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.phone || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email
                          </Typography>
                          <Typography variant="body2">{invoice.vendor_details.email || 'N/A'}</Typography>
                        </Grid>
                        {(invoice.vendor_details.address1 || invoice.vendor_details.city) && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Address
                            </Typography>
                            <Typography variant="body2">
                              {[
                                invoice.vendor_details.address1,
                                invoice.vendor_details.address2,
                                [invoice.vendor_details.city, invoice.vendor_details.state, invoice.vendor_details.zip]
                                  .filter(Boolean)
                                  .join(', '),
                              ]
                                .filter(Boolean)
                                .join(', ') || 'N/A'}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Embedded PDF */}
        {invoice.pdf_url && (
          <Grid item xs={12} md={7}>
            <Card sx={{ height: '100%', minHeight: 700 }}>
              <CardContent sx={{ height: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title={invoice.pdf_filename || 'Invoice PDF'}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 700,
                      border: 'none',
                    }}
                  />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={700}>
                    <CircularProgress />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Extracted Text Modal */}
      <Dialog open={extractedTextModalOpen} onClose={() => setExtractedTextModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">Extracted Text</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant={viewMode === 'text' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('text')}
                >
                  View as Text
                </Button>
                <Button
                  variant={viewMode === 'json' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('json')}
                  disabled={!invoice.textract_response}
                >
                  View as JSON
                </Button>
              </Box>
            </Box>
            <IconButton edge="end" onClick={() => setExtractedTextModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            value={
              viewMode === 'text'
                ? invoice.raw_extracted_text || 'No extracted text available'
                : invoice.textract_response
                  ? JSON.stringify(invoice.textract_response, null, 2)
                  : 'No JSON data available'
            }
            fullWidth
            multiline
            rows={20}
            variant="outlined"
            InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtractedTextModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Match Detail Modal */}
      <Dialog
        open={matchDetailModal.open}
        onClose={() => setMatchDetailModal({ open: false, type: null, data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {matchDetailModal.type === 'servman' ? 'ServMan Invoice Details' : 'DocuWare Invoice Details'}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => setMatchDetailModal({ open: false, type: null, data: null })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {matchDetailModal.data ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Location
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.location || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Vendor ID
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.vendor_id || 'N/A'}</Typography>
              </Grid>
              {matchDetailModal.type === 'servman' && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendor Name
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.vendor_name || 'N/A'}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Number
                </Typography>
                <Typography variant="body1">{matchDetailModal.data.invoice_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Date
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.invoice_date
                    ? new Date(matchDetailModal.data.invoice_date).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Invoice Amount
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.invoice_amount != null
                    ? `$${parseFloat(matchDetailModal.data.invoice_amount.toString()).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : 'N/A'}
                </Typography>
              </Grid>
              {matchDetailModal.type === 'servman' && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Transaction Type
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.trantype || 'N/A'}</Typography>
                </Grid>
              )}
              {matchDetailModal.type === 'docuware' && matchDetailModal.data.docid && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    DocuWare Doc ID
                  </Typography>
                  <Typography variant="body1">{matchDetailModal.data.docid}</Typography>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Synced
                </Typography>
                <Typography variant="body1">
                  {matchDetailModal.data.synced_at ? new Date(matchDetailModal.data.synced_at).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDetailModal({ open: false, type: null, data: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Validations Section */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="600" gutterBottom>
                Invoice Validations
              </Typography>
              {invoice.validated_at && (
                <Typography variant="caption" color="text.secondary">
                  Last validated: {new Date(invoice.validated_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Summary */}
          {invoice.line_items &&
            invoice.line_items.length > 0 &&
            (() => {
              const total = invoice.line_items!.length;
              const isServmanOk = (li: any) => li.servman_invoice_id && !li.servman_amount_mismatch && !li.servman_vendor_mismatch;
              const isDocuwareOk = (li: any) => li.servman_docuware_invoice_id && !li.docuware_amount_mismatch && !li.docuware_vendor_mismatch;
              const servmanMatched = invoice.line_items!.filter(isServmanOk).length;
              const docuwareMatched = invoice.line_items!.filter(isDocuwareOk).length;
              const fullyMatched = invoice.line_items!.filter((li: any) => isServmanOk(li) && isDocuwareOk(li)).length;
              const unmatched = invoice.line_items!.filter((li: any) => !isServmanOk(li) || !isDocuwareOk(li)).length;
              const totalAmount = invoice.line_items!.reduce(
                (sum: number, li: any) => sum + (parseFloat(li.amount) || 0),
                0,
              );
              return (
                <Box mb={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Items
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {fullyMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fully Matched
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold" color="error.main">
                          {unmatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          With Issues
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {servmanMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ServMan
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          {docuwareMatched}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          DocuWare
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                          ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}

          {/* Toggle + Line Items Table */}
          {invoice.line_items && invoice.line_items.length > 0 ? (
            <>
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <FormControlLabel
                  control={
                    <Switch checked={hideMatched} onChange={(e) => setHideMatched(e.target.checked)} size="small" />
                  }
                  label="Show only items with issues"
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Invoice Number</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Amount</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Exists in ServMan</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Exists in DocuWare</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.line_items
                      .filter((lineItem: any) =>
                        hideMatched ? !lineItem.servman_invoice_id || !lineItem.servman_docuware_invoice_id || lineItem.servman_amount_mismatch || lineItem.docuware_amount_mismatch || lineItem.servman_vendor_mismatch || lineItem.docuware_vendor_mismatch : true,
                      )
                      .map((lineItem: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{lineItem.invoice_number || 'N/A'}</TableCell>
                          <TableCell>
                            {lineItem.amount
                              ? `$${parseFloat(lineItem.amount.toString()).toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            {lineItem.servman_invoice_id && !lineItem.servman_amount_mismatch && !lineItem.servman_vendor_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'servman',
                                    data: lineItem.servman_invoice,
                                  })
                                }
                              >
                                <CheckCircleIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main" sx={{ textDecoration: 'underline' }}>
                                  Yes
                                </Typography>
                              </Box>
                            ) : lineItem.servman_amount_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'servman',
                                    data: lineItem.servman_invoice,
                                  })
                                }
                              >
                                <WarningIcon fontSize="small" color="warning" />
                                <Typography variant="body2" color="warning.main" sx={{ textDecoration: 'underline' }}>
                                  Amount Mismatch
                                </Typography>
                              </Box>
                            ) : lineItem.servman_vendor_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'servman',
                                    data: lineItem.servman_invoice,
                                  })
                                }
                              >
                                <InfoIcon fontSize="small" color="info" />
                                <Typography variant="body2" color="info.main" sx={{ textDecoration: 'underline' }}>
                                  Wrong Vendor
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                <ErrorIcon fontSize="small" color="error" />
                                <Typography variant="body2" color="error.main">
                                  No
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {lineItem.servman_docuware_invoice_id && !lineItem.docuware_amount_mismatch && !lineItem.docuware_vendor_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'docuware',
                                    data: lineItem.servman_docuware_invoice,
                                  })
                                }
                              >
                                <CheckCircleIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main" sx={{ textDecoration: 'underline' }}>
                                  Yes
                                </Typography>
                              </Box>
                            ) : lineItem.docuware_amount_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'docuware',
                                    data: lineItem.servman_docuware_invoice,
                                  })
                                }
                              >
                                <WarningIcon fontSize="small" color="warning" />
                                <Typography variant="body2" color="warning.main" sx={{ textDecoration: 'underline' }}>
                                  Amount Mismatch
                                </Typography>
                              </Box>
                            ) : lineItem.docuware_vendor_mismatch ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setMatchDetailModal({
                                    open: true,
                                    type: 'docuware',
                                    data: lineItem.servman_docuware_invoice,
                                  })
                                }
                              >
                                <InfoIcon fontSize="small" color="info" />
                                <Typography variant="body2" color="info.main" sx={{ textDecoration: 'underline' }}>
                                  Wrong Vendor
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                <ErrorIcon fontSize="small" color="error" />
                                <Typography variant="body2" color="error.main">
                                  No
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    {invoice.line_items.filter((lineItem: any) =>
                      hideMatched ? !lineItem.servman_invoice_id || !lineItem.servman_docuware_invoice_id || lineItem.servman_amount_mismatch || lineItem.docuware_amount_mismatch || lineItem.servman_vendor_mismatch || lineItem.docuware_vendor_mismatch : true,
                    ).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            All line items are fully matched. Toggle off the filter to see all items.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="info">No line items available for validation</Alert>
          )}

          {/* Action Buttons */}
          <Box mt={3}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="flex-start" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRevalidate}
                disabled={revalidating}
                startIcon={revalidating ? <CircularProgress size={20} /> : undefined}
              >
                {revalidating ? 'Re-validating...' : 'Re-validate Invoice'}
              </Button>
              {invoice.validation_status !== 'ignored' && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleMarkAsIgnored}
                  startIcon={<BlockIcon />}
                >
                  Mark as Ignored
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorInvoiceDetailPage;
